/* eslint-disable @typescript-eslint/camelcase */
import { Grid, IconButton, makeStyles } from '@material-ui/core';
import { InfoOutlined as InfoOutlinedIcon } from '@material-ui/icons';
import InputIcon from '@material-ui/icons/Input';
import PageHeader from 'components/PageHeader';
import SearchFilterBar from 'components/SearchFilterBar';
import TableItems, {
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { selectCompany } from 'modules/authenticate/actions';
import { CompanyData } from 'modules/authenticate/interfaces';
import { getAllCompaniesOperatorRequest } from 'modules/companies/actions';
import {
  AllCompaniesFields,
  ListAllCompaniesDataProps,
} from 'modules/companies/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { getCompanyName } from 'utils/tsHelpers';
import { filterOptions } from './filterOptions';
import styles from './styles';
import { headers } from './tableHeaders';

const useStyles = makeStyles(styles);

interface Props {
  companiesList: ListAllCompaniesDataProps;
  isLoading: boolean;
  getAllCompaniesOperatorRequest: (props) => void;
  selectCompany: (company: CompanyData | null) => void;
}

const AllCompaniesList = ({
  companiesList,
  isLoading,
  getAllCompaniesOperatorRequest,
  selectCompany,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    setQuery,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    setSearchValue,
  } = useTableFilters();

  const history = useHistory();
  const [showTable, setShowTable] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);

  const getCompanies = React.useCallback(
    (refresh = false) => {
      if (!showTable) return;

      getAllCompaniesOperatorRequest({
        ...query,
      });

      setRefresh(refresh);
    },
    [getAllCompaniesOperatorRequest, query, showTable]
  );

  React.useEffect(() => {
    getCompanies();
  }, [getCompanies]);

  const handleClick = (item: AllCompaniesFields) => {
    selectCompany({
      id: item?.company_uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    history.push('/dashboard/partner');
  };

  return (
    <Grid container>
      <PageHeader
        title={i18n.t('COMPANIES_PAGE.LIST_COMPANIES_TITLE_KEY')}
        subtitle={i18n.t('COMPANIES_PAGE.LIST_COMPANIES_SUBTITLE_KEY')}
      />
      <Grid container className={classes.searchContainer}>
        <SearchFilterBar
          filterOptions={filterOptions}
          isLoading={isLoading}
          searchValue={searchValue}
          query={query}
          onApplyFilters={() => setShowTable(true)}
          setSearchValue={setSearchValue}
          setQuery={setQuery}
        />

        {showTable && (
          <TableItems
            header={
              <>
                {headers.map(item => (
                  <TableCell
                    centerTitle={item?.centerTitle}
                    key={item?.i18n}
                    markOderItem={itemOrder === item?.key}
                    order={item?.order}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                  >
                    {i18n.t(item?.i18n)}
                  </TableCell>
                ))}
                <Permission value="list_company_details">
                  <TableCell centerTitle key="company_details">
                    {i18n.t('DETAILS_KEY')}
                  </TableCell>
                </Permission>
              </>
            }
            renderItems={(item: AllCompaniesFields, index) => (
              <TableRow key={item?.company_uuid}>
                <TableCell
                  maxLength={25}
                  ellipsis
                  centerTitle
                  className={classes.centerFields}
                >
                  <IconButton
                    id={`access-company-${index}`}
                    onClick={() => {
                      handleClick(item);
                    }}
                  >
                    <InputIcon className={classes.accessButton} />
                  </IconButton>
                </TableCell>
                <TableCell ellipsis className={classes.companyName}>
                  {getCompanyName(item)}
                </TableCell>
                <TableCell maxLength={25} ellipsis align="center">
                  {item?.num_partners}
                </TableCell>
                <TableCell maxLength={25} ellipsis centerTitle align="center">
                  {item?.num_environments}
                </TableCell>
                <TableCell maxLength={25} ellipsis centerTitle align="center">
                  {item?.num_clients}
                </TableCell>
                <TableCell maxLength={25} ellipsis centerTitle align="center">
                  {item?.num_users}
                </TableCell>
                <TableCell
                  centerTitle
                  tooltip={i18n.t(
                    item?.portal_deleted
                      ? 'COMPANIES_PAGE.PORTAL_DELETED_TEXT_KEY'
                      : 'ACTIVE_KEY'
                  )}
                >
                  <Grid container justify="center">
                    <StatusIcon
                      status={item?.portal_deleted ? 'blocked' : 'success'}
                    />
                  </Grid>
                </TableCell>
                <Permission value="list_company_details">
                  <TableCell className={classes.center}>
                    <Link to={`/companies/${item?.company_uuid}/edit`}>
                      <InfoOutlinedIcon className={classes.editIcon} />
                    </Link>
                  </TableCell>
                </Permission>
              </TableRow>
            )}
            data={companiesList}
            isLoading={!refresh && isLoading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('NO_COMPANY_KEY')}
          />
        )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  companies: { isLoading, companiesList },
}: ApplicationState) => ({
  isLoading,
  companiesList,
});

export default connect(mapStateToProps, {
  selectCompany,
  getAllCompaniesOperatorRequest,
})(AllCompaniesList);
